import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { useLoggedMutation } from "@/hooks/use-action-log-session";
import {
  DEFAULT_TEXT_FONT_SIZE,
  useMeasurementTextLock,
} from "@/hooks/useMeasurementTextLock";
import { notifyError } from "@/lib/notify-error";

export function useTakeoffTextSettings({
  jobId,
  planDocumentId,
  planRevisionId,
  persistedTextLocked,
  persistedTextFontSize,
  canEdit,
}: {
  jobId: Id<"takeoffPilotJobs">;
  planDocumentId?: Id<"takeoffPilotPlanDocuments">;
  planRevisionId?: Id<"projectDocumentRevisions">;
  persistedTextLocked?: boolean;
  persistedTextFontSize?: number;
  canEdit: boolean;
}) {
  const { textLocked, setTextLock, textFontSize, setTextFontSize, settingsLoaded } =
    useMeasurementTextLock({
      jobId,
      planDocumentId,
      planRevisionId,
      persistedTextLocked,
      persistedTextFontSize,
    });
  const updateSettings = useLoggedMutation(
    api.takeoffPilot.jobs.updatePlanDocumentTextSettings,
  );
  const lockVersion = useRef(0);
  const fontVersion = useRef(0);
  const migrationKey = useRef<string | null>(null);
  const confirmedLock = useRef(persistedTextLocked ?? false);
  const confirmedFont = useRef(persistedTextFontSize ?? DEFAULT_TEXT_FONT_SIZE);
  const scope = `${jobId}:${planDocumentId ?? ""}:${planRevisionId ?? ""}`;
  const scopeGeneration = useRef({ scope, value: 0 });
  useLayoutEffect(() => {
    if (scopeGeneration.current.scope !== scope) {
      scopeGeneration.current = { scope, value: scopeGeneration.current.value + 1 };
    }
  }, [scope]);

  useEffect(() => {
    if (!settingsLoaded) return;
    if (persistedTextLocked !== undefined) confirmedLock.current = persistedTextLocked;
    if (persistedTextFontSize !== undefined) confirmedFont.current = persistedTextFontSize;
  }, [persistedTextFontSize, persistedTextLocked, settingsLoaded]);

  useEffect(() => {
    if (
      !canEdit ||
      !settingsLoaded ||
      !planDocumentId ||
      migrationKey.current === planDocumentId ||
      (persistedTextLocked !== undefined && persistedTextFontSize !== undefined)
    ) return;
    migrationKey.current = planDocumentId;
    confirmedLock.current = textLocked;
    confirmedFont.current = textFontSize;
    void updateSettings({ planDocumentId, textLocked, textFontSize }).catch((error) => {
      console.error("Failed to migrate annotation text settings", error);
    });
  }, [
    canEdit,
    persistedTextFontSize,
    persistedTextLocked,
    planDocumentId,
    settingsLoaded,
    textFontSize,
    textLocked,
    updateSettings,
  ]);

  const handleTextLockToggle = useCallback(() => {
    if (!planDocumentId) return;
    const next = !textLocked;
    const version = ++lockVersion.current;
    const generation = scopeGeneration.current.value;
    setTextLock(next);
    void updateSettings({ planDocumentId, textLocked: next })
      .then(() => {
        if (scopeGeneration.current.value === generation) confirmedLock.current = next;
      })
      .catch((error) => {
        console.error(error);
        if (scopeGeneration.current.value !== generation || lockVersion.current !== version) return;
        setTextLock(confirmedLock.current);
        notifyError(error, "Failed to save annotation lock setting");
      });
  }, [planDocumentId, scope, setTextLock, textLocked, updateSettings]);

  const handleTextFontSizeChange = useCallback(
    (next: number) => {
      if (!planDocumentId) return;
      const version = ++fontVersion.current;
      const generation = scopeGeneration.current.value;
      setTextFontSize(next);
      void updateSettings({ planDocumentId, textFontSize: next })
        .then(() => {
          if (scopeGeneration.current.value === generation) confirmedFont.current = next;
        })
        .catch((error) => {
          console.error(error);
          if (scopeGeneration.current.value !== generation || fontVersion.current !== version) return;
          setTextFontSize(confirmedFont.current);
          notifyError(error, "Failed to save annotation text size");
        });
    },
    [planDocumentId, scope, setTextFontSize, updateSettings],
  );

  return { textLocked, textFontSize, handleTextLockToggle, handleTextFontSizeChange };
}
